import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import {ContenedoresSubscribe} from "./contenedores/ContenedoresSubscribe";
import { PlatformApiClient, PlatformApiError } from "./api";
import { loadPlatformWebConfig } from "./config";
import { listedProducts, productState } from "./product-access";
import { productPresentationForRouteBase } from "./product-presentation";
import { canonicalOrganizationPath, loginPath, parseRoute, safeReturnPath, type AppRoute } from "./routes";
import { hasProductRuntime, renderProductRuntime } from "./product-runtime";
import { MetaWhatsAppReviewPanel } from "./MetaWhatsAppReviewPanel";
import type { OrganizationContext, ResolvedOrganizationContext, Session, ShellContext } from "./types";
import { ActionBar, AdaptiveGrid, DetailsList, Page, PageHeader, Section, SectionHeader, StatusState } from "./adaptive/primitives";
import { CrmWorkspace } from "./crm/CrmWorkspace";
import { DemosWorkspace } from "./demos/DemosWorkspace";
import { DemoDetailWorkspace } from "./demos/DemoDetailWorkspace";
import { CasaNativaAdmin } from "./demos/gastronomy/reference-restaurant/CasaNativaAdmin";
import { ProspectDemoWorkspace } from "./demos/ProspectDemoWorkspace";
import { SolutionStudio } from "./solution-studio/SolutionStudio";
import { experienceDefinitionForRouteBase, experienceEntryPath, experienceFor } from "./experience";
import { ProductShell } from "./ProductShell";
import { ReviewBoostBillingPanel } from "./signup/ReviewBoostBillingPanel";
import { reviewBoostPilotVisible } from "./signup/ReviewBoostSignupPilot";
import { SupportModeBanner } from "./SupportModeBanner";
import {
  GASTRO_SALES_DEMO_SECTIONS,
  GastroSalesDemo,
  canAccessGastroSalesDemo,
  gastroSalesDemoPath,
  parseGastroSalesDemoSection,
} from "./gastro-sales-demo/GastroSalesDemo";

type AuthState =
  | { kind: "loading" }
  | { kind: "signed-out" }
  | { kind: "ready"; session: Session; context: ShellContext }
  | { kind: "error"; message: string };

export function selectableOrganizations(context: ShellContext): readonly OrganizationContext[] {
  return context.organizations.filter(({ organization }) => organization.status === "active");
}

export function isPlatformStaff(context: ShellContext): boolean {
  return context.platformStaff === true || context.organizations.some(({ membership }) => membership.status === "active" && membership.role === "platform_staff");
}

export function loginErrorMessage(cause: unknown): string {
  if (cause instanceof PlatformApiError && cause.status === 429) return "Too many sign-in attempts. Wait a few minutes and try again.";
  if (cause instanceof PlatformApiError && cause.status === 403) return "The sign-in request was rejected. Refresh and try again.";
  return "Email or password is incorrect.";
}

function currentRoute(): AppRoute {
  return parseRoute(window.location.pathname, window.location.search);
}

function useRoute(): [AppRoute, (path: string, replace?: boolean) => void] {
  const [route, setRoute] = useState(currentRoute);
  useEffect(() => {
    const update = () => {
      // Workspace guards must decide before the router snapshots a history URL.
      if (!window.dispatchEvent(new Event("clickdialog:before-history-navigation", { cancelable: true }))) return;
      setRoute(currentRoute());
    };
    window.addEventListener("popstate", update);
    return () => window.removeEventListener("popstate", update);
  }, []);
  const navigate = useCallback((path: string, replace = false) => {
    if (replace) window.history.replaceState(null, "", path);
    else window.history.pushState(null, "", path);
    setRoute(currentRoute());
  }, []);
  return [route, navigate];
}

export function StatusPage({ eyebrow, title, message, action }: { eyebrow: string; title: string; message: string; action?: React.ReactNode }) {
  return <main className="status-page" id="main-content" tabIndex={-1} aria-live="polite">
    <StatusState className="status-card" eyebrow={eyebrow} title={title} message={message} action={action} />
  </main>;
}

export function Login({ returnTo, api, onReady }: { returnTo: string; api: PlatformApiClient; onReady: (context: ShellContext, session: Session, destination: string) => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const logged = await api.login(email, password);
      const context = await api.context();
      const available = selectableOrganizations(context);
      const fallback = isPlatformStaff(context)
        ? "/crm"
        : available.length === 1
          ? `/o/${available[0]!.organization.slug}/home`
          : "/select-organization";
      onReady(context, logged.session, safeReturnPath(returnTo) ?? fallback);
    } catch (cause) {
      setError(loginErrorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  return <main className="login-page" id="main-content" tabIndex={-1}>
    <section className="login-brand" aria-label="ClickDialog Platform">
      <div className="brand-mark" aria-hidden="true">CD</div>
      <p className="eyebrow light">ClickDialog Platform</p>
      <h1>One workspace.<br />Every customer conversation.</h1>
      <p>Manage your organization and move between ClickDialog products from one secure place.</p>
    </section>
    <section className="login-panel">
      <form className="auth-card" onSubmit={submit}>
        <p className="eyebrow">Private workspace</p>
        <h2>Welcome back</h2>
        <p className="muted">Sign in with your Platform account.</p>
        {error && <div className="inline-alert" role="alert">{error}</div>}
        <label htmlFor="email">Email address</label>
        <input id="email" name="email" type="email" autoComplete="username" required value={email} onChange={(event) => setEmail(event.target.value)} />
        <label htmlFor="password">Password</label>
        <input id="password" name="password" type="password" autoComplete="current-password" required value={password} onChange={(event) => setPassword(event.target.value)} />
        <button className="primary-button" disabled={busy} type="submit">{busy ? "Signing in…" : "Sign in"}</button>
        <p className="security-note">Your session stays in a secure, host-only cookie. This browser never stores a bearer token.</p>
        {reviewBoostPilotVisible(loadPlatformWebConfig().environment,import.meta.env?.VITE_CLICKDIALOG_REVIEW_BOOST_SIGNUP_PILOT,import.meta.env?.VITE_CLICKDIALOG_REVIEW_BOOST_AR_PRODUCTION_APPROVED) &&
          <p className="security-note">¿Todavía no tenés cuenta? <a href="/signup">Empezar con Review Boost</a></p>}
      </form>
    </section>
  </main>;
}

function OrganizationPicker({ context, navigate }: { context: ShellContext; navigate: (path: string) => void }) {
  const available = selectableOrganizations(context);
  const staffNavigation = isPlatformStaff(context) && !context.supportSession;
  return <ShellFrame context={context} navigate={navigate}>
    <Page>
      <PageHeader eyebrow="Organizations" title="Choose a workspace" description="Select the organization you want to manage." actions={staffNavigation ? <div className="button-row"><button onClick={() => navigate("/demos")}>Abrir Demos</button><button className="primary-button" onClick={() => navigate("/crm")}>Abrir CRM</button></div> : undefined} />
      {available.length === 0
        ? <EmptyState title="No active organizations" message={staffNavigation ? "No hay espacios activos disponibles. Las herramientas internas de ClickDialog continúan accesibles." : "Your account does not have an active, available organization. Ask a Platform administrator for access."} />
        : <AdaptiveGrid className="organization-grid" compact={1} medium={2} wide={3}>
          {available.map(({ organization, membership }) => <button className="organization-card" key={organization.id} onClick={() => navigate(`/o/${organization.slug}/home`)}>
            <span className="organization-avatar" aria-hidden="true">{organization.displayName.slice(0, 2).toUpperCase()}</span>
            <span><strong>{organization.displayName}</strong><small>{membership.role.replace("_", " ")} · {organization.status}</small></span>
            <span aria-hidden="true">→</span>
          </button>)}
        </AdaptiveGrid>}
    </Page>
  </ShellFrame>;
}

function EmptyState({ title, message }: { title: string; message: string }) {
  return <StatusState className="empty-state" title={title} message={message} />;
}

function ShellFrame({ context, navigate, organization, children }: { context: ShellContext; navigate: (path: string) => void; organization?: OrganizationContext; children: React.ReactNode }) {
  const products = organization ? listedProducts(organization.products).filter(({ access }) => access.allowed) : [];
  const pathname = window.location.pathname;
  const staff = isPlatformStaff(context);
  const staffNavigation = staff && !context.supportSession;
  const salesDemoAllowed = Boolean(organization) && canAccessGastroSalesDemo(staff, Boolean(context.supportSession));
  const salesDemoSection = salesDemoAllowed ? parseGastroSalesDemoSection(window.location.search) : null;
  const salesDemoActive = salesDemoSection !== null;
  const staffArea = pathname === "/crm" || pathname.startsWith("/crm/");
  const demosArea = pathname === "/demos" || pathname.startsWith("/demos/");
  const solutionStudioArea = pathname === "/solution-studio";
  const slug = organization?.organization.slug;
  const homePath = slug ? `/o/${slug}/home` : "";
  const settingsPath = slug ? `/o/${slug}/settings` : "";
  const availableOrganizations = selectableOrganizations(context);
  const globalHomePath = availableOrganizations.length === 1
    ? `/o/${availableOrganizations[0]!.organization.slug}/home`
    : "/select-organization";
  const shellHomePath = salesDemoActive && slug
    ? gastroSalesDemoPath(slug, "today")
    : solutionStudioArea && staffNavigation
      ? "/solution-studio"
      : demosArea && staffNavigation
        ? "/demos"
        : staffArea && staffNavigation
          ? "/crm"
          : organization
            ? homePath
            : globalHomePath;
  const isCurrent = (path: string, descendants = false) => Boolean(path) && (pathname === path || (descendants && pathname.startsWith(`${path}/`)));

  const experience = !salesDemoActive ? experienceFor(organization, pathname) : null;
  if (experience && organization) {
    return <ProductShell
      experience={experience}
      organization={organization}
      context={context}
      navigate={navigate}
      staffNavigation={staffNavigation}
    >
      {children}
    </ProductShell>;
  }

  return <div className="app-shell">
    <a className="skip-link" href="#main-content">Saltar al contenido</a>
    <aside className="sidebar">
      <button className="brand-button" onClick={() => navigate(shellHomePath)} aria-label="Inicio ClickDialog">
        <span className="brand-mark small" aria-hidden="true">CD</span><span>ClickDialog</span>
      </button>
      {organization && salesDemoActive && slug && <nav aria-label="Demo gastronómica" className="side-nav" data-nav-kind="global">
        <p className="nav-label">Demo gastronómica</p>
        {GASTRO_SALES_DEMO_SECTIONS.map((item) => {
          const path = gastroSalesDemoPath(slug, item.key);
          return <a key={item.key} href={path} aria-current={salesDemoSection === item.key ? "page" : undefined} onClick={(event) => { event.preventDefault(); navigate(path); }}>{item.label}</a>;
        })}
        <p className="nav-label">Demos</p>
        <a href="/demos" onClick={(event) => { event.preventDefault(); navigate("/demos"); }}>Volver al catálogo</a>
      </nav>}
      {organization && !salesDemoActive && <nav aria-label="Navegación del espacio" className="side-nav" data-nav-kind="global">
        <p className="nav-label">Espacio</p>
        <a href={homePath} aria-current={isCurrent(homePath) ? "page" : undefined} onClick={(e) => { e.preventDefault(); navigate(e.currentTarget.pathname); }}>Inicio</a>
        {products.length > 0 && <>
          <p className="nav-label">Productos</p>
          {products.map(({ manifest }) => {
            const productPath = `/o/${organization.organization.slug}/${manifest.routeBase}`;
            return <a key={manifest.key} href={productPath} aria-current={isCurrent(productPath, true) ? "page" : undefined} onClick={(e) => { e.preventDefault(); navigate(e.currentTarget.pathname); }}>{manifest.displayName}</a>;
          })}
        </>}
        <p className="nav-label">Gestión</p>
        <a href={settingsPath} aria-current={isCurrent(settingsPath) ? "page" : undefined} onClick={(e) => { e.preventDefault(); navigate(e.currentTarget.pathname); }}>Configuración del negocio</a>
        {staffNavigation && <><p className="nav-label">ClickDialog</p><a href="/solution-studio" aria-current={solutionStudioArea ? "page" : undefined} onClick={(event) => { event.preventDefault(); navigate("/solution-studio"); }}>Solution Studio</a><a href="/demos" aria-current={demosArea ? "page" : undefined} onClick={(event) => { event.preventDefault(); navigate("/demos"); }}>Demos</a><a href={gastroSalesDemoPath(organization.organization.slug)} onClick={(event) => { event.preventDefault(); navigate(gastroSalesDemoPath(organization.organization.slug)); }}>Demo gastronómica · alias</a><a href="/crm" aria-current={staffArea ? "page" : undefined} onClick={(e) => { e.preventDefault(); navigate("/crm"); }}>CRM interno</a></>}
      </nav>}
      {!organization && staffNavigation && <nav aria-label="Operación ClickDialog" className="side-nav" data-nav-kind="global">
        <p className="nav-label">Operación ClickDialog</p>
        <a href="/crm" aria-current={pathname === "/crm" ? "page" : undefined} onClick={(e) => { e.preventDefault(); navigate("/crm"); }}>Hoy</a>
        <a href="/crm/businesses" aria-current={isCurrent("/crm/businesses", true) ? "page" : undefined} onClick={(e) => { e.preventDefault(); navigate("/crm/businesses"); }}>Negocios</a>
        <a href="/crm/pipeline" aria-current={isCurrent("/crm/pipeline") ? "page" : undefined} onClick={(e) => { e.preventDefault(); navigate("/crm/pipeline"); }}>Pipeline</a>
        <a href="/crm/tasks" aria-current={isCurrent("/crm/tasks") ? "page" : undefined} onClick={(e) => { e.preventDefault(); navigate("/crm/tasks"); }}>Tareas</a>
        <p className="nav-label">Platform</p>
        <a href="/solution-studio" aria-current={solutionStudioArea ? "page" : undefined} onClick={(e) => { e.preventDefault(); navigate("/solution-studio"); }}>Solution Studio</a>
        <a href="/demos" aria-current={demosArea ? "page" : undefined} onClick={(e) => { e.preventDefault(); navigate("/demos"); }}>Demos</a>
        <a href={globalHomePath} onClick={(e) => { e.preventDefault(); navigate(globalHomePath); }}>Espacios de clientes</a>
      </nav>}
      <div className="sidebar-footer"><span>{context.user.displayName}</span><small>{context.user.email}</small></div>
    </aside>

    <div className="shell-main">
      <SupportModeBanner context={context} organization={organization} />
      <header className="topbar" data-shell-region="topbar">
        {organization
          ? <label className="organization-switcher">Negocio<select aria-label="Cambiar negocio" value={organization.organization.slug} onChange={(e) => navigate(`/o/${e.target.value}/home`)}>{availableOrganizations.map(({ organization: item }) => <option key={item.id} value={item.slug}>{item.displayName}</option>)}</select></label>
          : <button className="topbar-title topbar-home-button" type="button" onClick={() => navigate(solutionStudioArea && staffNavigation ? "/solution-studio" : demosArea && staffNavigation ? "/demos" : staffArea && staffNavigation ? "/crm" : globalHomePath)} aria-label="Volver al espacio ClickDialog">{solutionStudioArea && staffNavigation ? "ClickDialog Solution Studio" : demosArea && staffNavigation ? "ClickDialog Demos" : staffArea && staffNavigation ? "ClickDialog CRM" : "ClickDialog"}</button>}
        <button className="account-button" onClick={() => navigate("/account")} aria-label="Abrir mi cuenta"><span aria-hidden="true">{context.user.displayName.slice(0, 1).toUpperCase()}</span>{context.user.displayName}</button>
      </header>

      {organization && salesDemoActive && slug && <nav className="mobile-nav" aria-label="Navegación móvil de la demo gastronómica" data-nav-kind="global">
        {GASTRO_SALES_DEMO_SECTIONS.map((item) => {
          const path = gastroSalesDemoPath(slug, item.key);
          return <a key={item.key} href={path} aria-current={salesDemoSection === item.key ? "page" : undefined} onClick={(event) => { event.preventDefault(); navigate(path); }}>{item.label}</a>;
        })}
        <a href="/demos" onClick={(event) => { event.preventDefault(); navigate("/demos"); }}>Catálogo</a>
      </nav>}
      {organization && !salesDemoActive && <nav className="mobile-nav" aria-label="Navegación móvil del espacio" data-nav-kind="global">
        <a href={homePath} aria-current={isCurrent(homePath) ? "page" : undefined} onClick={(event) => { event.preventDefault(); navigate(event.currentTarget.pathname); }}>Inicio</a>
        {products.map(({ manifest }) => {
          const productPath = `/o/${organization.organization.slug}/${manifest.routeBase}`;
          return <a key={manifest.key} href={productPath} aria-current={isCurrent(productPath, true) ? "page" : undefined} onClick={(event) => { event.preventDefault(); navigate(event.currentTarget.pathname); }}>{manifest.displayName}</a>;
        })}
        <a href={settingsPath} aria-current={isCurrent(settingsPath) ? "page" : undefined} onClick={(event) => { event.preventDefault(); navigate(event.currentTarget.pathname); }}>Configuración</a>
        {staffNavigation && <a href="/solution-studio" aria-current={solutionStudioArea ? "page" : undefined} onClick={(event) => { event.preventDefault(); navigate("/solution-studio"); }}>Studio</a>}
        {staffNavigation && <a href="/demos" onClick={(event) => { event.preventDefault(); navigate("/demos"); }}>Demos</a>}
        {staffNavigation && <a href={gastroSalesDemoPath(organization.organization.slug)} onClick={(event) => { event.preventDefault(); navigate(gastroSalesDemoPath(organization.organization.slug)); }}>Demo Gastro</a>}
        {staffNavigation && <a href="/crm" aria-current={staffArea ? "page" : undefined} onClick={(event) => { event.preventDefault(); navigate("/crm"); }}>CRM</a>}
      </nav>}
      {!organization && staffNavigation && <nav className="mobile-nav crm-global-mobile-nav" aria-label="Navegación móvil interna" data-nav-kind="global">
        <a href="/crm" aria-current={pathname === "/crm" ? "page" : undefined} onClick={(event) => { event.preventDefault(); navigate("/crm"); }}>Hoy</a>
        <a href="/crm/businesses" aria-current={isCurrent("/crm/businesses", true) ? "page" : undefined} onClick={(event) => { event.preventDefault(); navigate("/crm/businesses"); }}>Negocios</a>
        <a href="/crm/pipeline" aria-current={isCurrent("/crm/pipeline") ? "page" : undefined} onClick={(event) => { event.preventDefault(); navigate("/crm/pipeline"); }}>Pipeline</a>
        <a href="/crm/tasks" aria-current={isCurrent("/crm/tasks") ? "page" : undefined} onClick={(event) => { event.preventDefault(); navigate("/crm/tasks"); }}>Tareas</a>
        <a href="/solution-studio" aria-current={solutionStudioArea ? "page" : undefined} onClick={(event) => { event.preventDefault(); navigate("/solution-studio"); }}>Studio</a>
        <a href="/demos" aria-current={demosArea ? "page" : undefined} onClick={(event) => { event.preventDefault(); navigate("/demos"); }}>Demos</a>
      </nav>}

      <main id="main-content" className="content" data-shell-region="content" tabIndex={-1}>{children}</main>
    </div>
  </div>;
}

function platformProductStateLabel(state: ReturnType<typeof productState>): string {
  if (state === "available") return "Listo para usar";
  if (state === "suspended") return "Requiere atención";
  if (state === "adapter-pending") return "Configuración pendiente";
  if (state === "not-bound") return "Vinculación pendiente";
  return "No disponible";
}

function OrganizationHome({ organization, navigate, salesDemoAllowed }: { organization: OrganizationContext; navigate: (path: string) => void; salesDemoAllowed: boolean }) {
  const demoSection = salesDemoAllowed ? parseGastroSalesDemoSection(window.location.search) : null;
  if (demoSection) return <GastroSalesDemo organizationName={organization.organization.displayName} organizationSlug={organization.organization.slug} section={demoSection} navigate={navigate} />;

  const products = listedProducts(organization.products);
  const availableCount = products.filter((product) =>
    productState(product.manifest, product, hasProductRuntime(product.manifest.routeBase)) === "available"
  ).length;

  return <Page className="platform-home">
    <PageHeader
      eyebrow="ClickDialog Platform"
      title={organization.organization.displayName}
      description={products.length === 0
        ? "Este es el espacio principal de tu negocio en ClickDialog."
        : `${availableCount} de ${products.length} ${products.length === 1 ? "producto disponible" : "productos disponibles"} en este espacio.`}
      actions={salesDemoAllowed ? <div className="button-row platform-home-staff-actions"><button onClick={() => navigate("/demos")}>Demos</button><button onClick={() => navigate(gastroSalesDemoPath(organization.organization.slug))}>Demo gastronómica</button></div> : undefined}
    />

    <Section className="platform-home-products" labelledBy="products-title">
      <SectionHeader
        eyebrow="Tus productos"
        title="Entrá donde querés trabajar"
        titleId="products-title"
        description="Cada producto tiene su propio espacio de trabajo. ClickDialog Platform mantiene tu organización, cuenta y acceso."
      />

      {products.length === 0
        ? <EmptyState title="Todavía no hay productos activos" message="Cuando se habilite un producto para este negocio, va a aparecer acá." />
        : <div className="platform-product-list">
          {products.map((product) => {
            const { manifest } = product;
            const definition = experienceDefinitionForRouteBase(manifest.routeBase);
            const state = productState(manifest, product, hasProductRuntime(manifest.routeBase));
            const productName = definition?.displayName ?? manifest.displayName;
            const productPath = definition && state === "available"
              ? experienceEntryPath(organization, definition)
              : `/o/${organization.organization.slug}/${manifest.routeBase}`;

            return <article className="platform-product-row" data-product-state={state} key={manifest.key}>
              <div className="platform-product-accent" aria-hidden="true" />
              <div className="platform-product-copy">
                <div className="platform-product-heading">
                  <h3>{productName}</h3>
                  <span className="platform-product-status">{platformProductStateLabel(state)}</span>
                </div>
                <p>{productPresentationForRouteBase(manifest.routeBase).description}</p>
              </div>
              {state === "available"
                ? <a className="platform-product-enter" href={productPath} onClick={(event) => { event.preventDefault(); navigate(productPath); }}>
                    Entrar <span aria-hidden="true">→</span>
                  </a>
                : <span className="platform-product-unavailable" aria-label={`${productName}: ${platformProductStateLabel(state)}`}>No disponible</span>}
            </article>;
          })}
        </div>}
    </Section>

    <div className="platform-home-management">
      <div>
        <p className="eyebrow">Administración</p>
        <strong>Organización y acceso</strong>
        <span>Configurá los datos e integraciones de este negocio sin mezclarlo con el trabajo de cada producto.</span>
      </div>
      <a href={`/o/${organization.organization.slug}/settings`} onClick={(event) => { event.preventDefault(); navigate(event.currentTarget.pathname); }}>Configuración del negocio</a>
    </div>
  </Page>;
}

const productMessages = {
  "adapter-pending": ["Integración pendiente", "El acceso está configurado, pero el adaptador no está activo."],
  "not-bound": ["Producto sin vincular", "Este negocio no tiene una vinculación activa con el producto."],
  suspended: ["Producto suspendido", "Este producto se encuentra temporalmente suspendido."],
  unavailable: ["Producto no disponible", "Este producto no está habilitado para este negocio."],
  available: ["Producto disponible", "Acceso y vinculación activos."],
} as const;

function ProductRoute({ organization, routeBase, nestedPath, registry, api, navigate, onExpired, user }: { organization: OrganizationContext; routeBase: string; nestedPath: string; registry: ShellContext["productRegistry"]; api: PlatformApiClient; navigate: (path: string) => void; onExpired: () => void; user?: ShellContext["user"] }) {
  const product = organization.products.find((item) => item.manifest.routeBase === routeBase);
  // The canary manifest is supplied only for a verified pilot organization.
  const manifest = product?.manifest ?? registry.find((item) => item.routeBase === routeBase);
  const runtimeAvailable = hasProductRuntime(routeBase);
  const state = productState(manifest, product, runtimeAvailable);
  const [title, message] = productMessages[state];
  if (state === "available") {
    const rendered = renderProductRuntime(routeBase, { organization, nestedPath, api, navigate, onExpired, user });
    if (rendered !== null) return rendered;
  }
  return <Page>
    <PageHeader eyebrow={manifest?.displayName ?? "Producto"} title={title} description={message} />
    <section className={`product-boundary ${state}`}>
      <span className="boundary-label">Espacio ClickDialog</span>
      <h2>{manifest?.displayName ?? routeBase}</h2>
      <DetailsList items={[
        { key: "organization", term: "Negocio", detail: organization.organization.displayName },
        { key: "state", term: "Estado", detail: state.replaceAll("-", " ") },
      ]} />
    </section>
  </Page>;
}

function OrganizationSettings({ organization, api, onExpired }: { organization: OrganizationContext; api: PlatformApiClient; onExpired: () => void }) {
  return <Page>
    <PageHeader eyebrow="Configuración" title="Configuración del negocio" description="Detalles del negocio e integraciones en ClickDialog." />
    <section className="details-card">
      <h2>Datos del espacio</h2>
      <DetailsList items={[
        { key: "name", term: "Nombre", detail: organization.organization.displayName },
        { key: "slug", term: "Identificador", detail: organization.organization.slug },
        { key: "status", term: "Estado", detail: organization.organization.status },
        { key: "role", term: "Tu rol", detail: organization.membership.role.replace("_", " ") },
      ]} />
    </section>
    {organization.contenedoresCanaryEligible && ["owner","admin"].includes(organization.membership.role) &&
      <ContenedoresSubscribe organization={organization} api={api} />}
    <MetaWhatsAppReviewPanel organization={organization} api={api} onExpired={onExpired} />
  </Page>;
}

function Account({ context, api, navigate, onSignedOut, onSecurityRefresh }: { context: ShellContext; api: PlatformApiClient; navigate: (path: string) => void; onSignedOut: () => void; onSecurityRefresh: (session: Session, context: ShellContext) => void }) {
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function password(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    const data = new FormData(event.currentTarget);
    try {
      const changed = await api.changePassword(String(data.get("currentPassword")), String(data.get("newPassword")));
      const liveContext = await api.context();
      onSecurityRefresh(changed.session, liveContext);
      event.currentTarget.reset();
      setNotice("Contraseña actualizada. Las demás sesiones fueron cerradas.");
    } catch (cause) {
      if (cause instanceof PlatformApiError && cause.status === 401) onSignedOut();
      else setError(cause instanceof Error ? cause.message : "No se pudo cambiar la contraseña.");
    } finally {
      setBusy(false);
    }
  }

  async function signOut(all: boolean) {
    setBusy(true);
    try {
      if (all) await api.logoutAll();
      else await api.logout();
    } finally {
      api.clearMemory();
      onSignedOut();
    }
  }

  return <ShellFrame context={context} navigate={navigate}>
    <Page>
      <PageHeader eyebrow="Mi Cuenta" title="Cuenta y seguridad" description="Gestioná tus credenciales de acceso y sesiones activas." />
      <AdaptiveGrid className="account-grid" compact={1} medium={2} wide={2}>
        <section className="details-card">
          <h2>Perfil</h2>
          <DetailsList items={[
            { key: "name", term: "Nombre", detail: context.user.displayName },
            { key: "email", term: "Email", detail: context.user.email },
            { key: "status", term: "Estado", detail: context.user.status },
          ]} />
        </section>

        <section className="details-card">
          <h2>Membresías activas</h2>
          {context.organizations.length === 0
            ? <p>No hay membresías activas.</p>
            : <DetailsList items={context.organizations.map(({ organization, membership }) => ({
              key: membership.id,
              term: organization.displayName,
              detail: `${membership.role.replace("_", " ")} · ${organization.status}`,
            }))} />}
        </section>

        <form className="details-card password-form" onSubmit={password}>
          <h2>Cambiar contraseña</h2>
          <p>Usá entre 12 y 128 caracteres. Al cambiarla se cerrarán las otras sesiones.</p>
          {notice && <div className="success-alert" role="status">{notice}</div>}
          {error && <div className="inline-alert" role="alert">{error}</div>}
          <label htmlFor="current-password">Contraseña actual</label>
          <input id="current-password" name="currentPassword" type="password" autoComplete="current-password" required />
          <label htmlFor="new-password">Nueva contraseña</label>
          <input id="new-password" name="newPassword" type="password" autoComplete="new-password" minLength={12} maxLength={128} required />
          <ActionBar sticky="compact"><button className="primary-button" disabled={busy}>Actualizar contraseña</button></ActionBar>
        </form>

        <section className="details-card danger-zone">
          <h2>Sesiones</h2>
          <p>Cerrá sesión en este navegador o revocá todos los accesos activos.</p>
          <ActionBar className="button-row" sticky="never" align="start">
            <button disabled={busy} onClick={() => void signOut(false)}>Cerrar sesión</button>
            <button className="danger-button" disabled={busy} onClick={() => void signOut(true)}>Cerrar sesión en todos los dispositivos</button>
          </ActionBar>
        </section>
      </AdaptiveGrid>
      {reviewBoostPilotVisible(loadPlatformWebConfig().environment,import.meta.env.VITE_CLICKDIALOG_REVIEW_BOOST_SIGNUP_PILOT,import.meta.env.VITE_CLICKDIALOG_REVIEW_BOOST_AR_PRODUCTION_APPROVED) &&
        !context.supportSession &&
        context.organizations.filter(({ membership,organization }) => membership.role==="owner" &&
          membership.status==="active" && organization.status==="active").map(({organization})=>(
          <ReviewBoostBillingPanel key={organization.id} organizationId={organization.id} organizationName={organization.displayName} api={api} />
        ))}
    </Page>
  </ShellFrame>;
}

function OrganizationRouteView({ route, context, api, navigate, onExpired }: { route: Extract<AppRoute, { kind: "organization" }>; context: ShellContext; api: PlatformApiClient; navigate: (path: string, replace?: boolean) => void; onExpired: () => void }) {
  const [state, setState] = useState<{ kind: "loading" } | { kind: "ready"; value: ResolvedOrganizationContext } | { kind: "error"; status: number }>({ kind: "loading" });

  useEffect(() => {
    let active = true;
    setState({ kind: "loading" });
    api.resolveOrganization(route.slug).then((value) => {
      if (!active) return;
      if (value.resolution.match === "alias") {
        navigate(canonicalOrganizationPath(window.location.pathname, value.organization.slug) + window.location.search + window.location.hash, true);
      }
      setState({ kind: "ready", value });
    }).catch((cause) => {
      if (!active) return;
      const status = cause instanceof PlatformApiError ? cause.status : 500;
      if (status === 401) onExpired();
      else setState({ kind: "error", status });
    });
    return () => { active = false; };
  }, [api, route.slug]);

  useEffect(() => {
    document.getElementById("main-content")?.focus({ preventScroll: true });
  }, [route.section, route.productRoute, state.kind]);

  if (state.kind === "loading") return <StatusPage eyebrow="Cargando espacio" title="Abriendo negocio…" message="Comprobando membresía y acceso a productos." />;
  if (state.kind === "error") return state.status === 403
    ? <StatusPage eyebrow="Acceso denegado" title="Acceso denegado al negocio" message={context.supportSession ? "El modo soporte sólo puede abrir la organización seleccionada desde el CRM." : "Tu sesión es válida, pero no tenés membresía activa para este negocio."} action={<button onClick={() => navigate("/select-organization")}>Volver</button>} />
    : state.status === 404
      ? <StatusPage eyebrow="No encontrado" title="Negocio no disponible" message="Este identificador de negocio no existe." action={<button onClick={() => navigate("/select-organization")}>Elegir un negocio</button>} />
      : <StatusPage eyebrow="No disponible" title="No se pudo cargar el espacio" message="El servicio está temporalmente no disponible. Intentá de nuevo en unos momentos." />;

  const organization = state.value;
  if (organization.organization.status !== "active") return <StatusPage eyebrow="Negocio no disponible" title={`${organization.organization.displayName} está ${organization.organization.status}`} message="Este espacio no puede abrirse mientras el negocio se encuentre inactivo." action={<button onClick={() => navigate("/select-organization")}>Elegir otro negocio</button>} />;

  const salesDemoAllowed = canAccessGastroSalesDemo(isPlatformStaff(context), Boolean(context.supportSession));
  return <ShellFrame context={context} navigate={navigate} organization={organization}>
    {route.section === "home"
      ? <OrganizationHome organization={organization} navigate={navigate} salesDemoAllowed={salesDemoAllowed} />
      : route.section === "settings"
        ? <OrganizationSettings organization={organization} api={api} onExpired={onExpired} />
        : <ProductRoute organization={organization} routeBase={route.productRoute!} nestedPath={route.nestedPath} registry={context.productRegistry} api={api} navigate={navigate} onExpired={onExpired} user={context.user} />}
  </ShellFrame>;
}

export function App({ client }: { client?: PlatformApiClient }) {
  const api = useMemo(() => client ?? new PlatformApiClient(fetch, loadPlatformWebConfig().apiBasePath), [client]);
  const [route, navigate] = useRoute();
  const [auth, setAuth] = useState<AuthState>({ kind: "loading" });

  useEffect(() => {
    let active = true;
    api.bootstrap().then(({ session, context }) => {
      if (active) setAuth({ kind: "ready", session: session.session, context });
    }).catch((cause) => {
      if (!active) return;
      if (cause instanceof PlatformApiError && cause.status === 401) setAuth({ kind: "signed-out" });
      else setAuth({ kind: "error", message: "The Platform service is unavailable." });
    });
    return () => { active = false; };
  }, [api]);

  useEffect(() => {
    if (auth.kind !== "ready" || route.kind !== "login") return;
    const available = selectableOrganizations(auth.context);
    const supportDestination = auth.context.supportSession && available.length === 1 ? `/o/${available[0]!.organization.slug}/home` : null;
    navigate(route.returnTo ?? supportDestination ?? (isPlatformStaff(auth.context) ? "/crm" : available.length === 1 ? `/o/${available[0]!.organization.slug}/home` : "/select-organization"), true);
  }, [auth, route, navigate]);

  useEffect(() => {
    if (auth.kind === "signed-out" && route.kind !== "login") navigate(loginPath(`${window.location.pathname}${window.location.search}`), true);
  }, [auth.kind, route.kind, navigate]);

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      document.getElementById("main-content")?.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [route]);

  function signedOut() {
    api.clearMemory();
    setAuth({ kind: "signed-out" });
    navigate(loginPath(window.location.pathname), true);
  }

  if (auth.kind === "loading") return <StatusPage eyebrow="ClickDialog Platform" title="Loading your workspace…" message="Verifying your secure session." />;
  if (auth.kind === "error") return <StatusPage eyebrow="Unavailable" title="Platform could not start" message={auth.message} action={<button onClick={() => window.location.reload()}>Try again</button>} />;
  if (auth.kind === "signed-out") {
    const returnTo = route.kind === "login" ? route.returnTo ?? "/select-organization" : `${window.location.pathname}${window.location.search}`;
    return <Login returnTo={returnTo} api={api} onReady={(context, session, destination) => { setAuth({ kind: "ready", context, session }); navigate(destination, true); }} />;
  }
  if (route.kind === "login") return <StatusPage eyebrow="ClickDialog Platform" title="Opening your workspace…" message="Your secure session is active." />;
  if (route.kind === "select-organization") return <OrganizationPicker context={auth.context} navigate={navigate} />;
  if (route.kind === "account") return <Account context={auth.context} api={api} navigate={navigate} onSignedOut={signedOut} onSecurityRefresh={(session, context) => setAuth({ kind: "ready", session, context })} />;
  if (route.kind === "demos") {
    if (auth.context.supportSession) return <ShellFrame context={auth.context} navigate={navigate}><Page><StatusState title="Modo soporte activo" message="Salí de la vista cliente antes de abrir el catálogo interno de demos." /></Page></ShellFrame>;
    if (!isPlatformStaff(auth.context)) return <StatusPage eyebrow="Acceso interno" title="Demos no disponible" message="Esta sección es exclusiva del equipo ClickDialog." action={<button onClick={() => navigate("/select-organization")}>Volver a mis negocios</button>} />;
    if (new URLSearchParams(window.location.search).get("reference") === "casa-nativa") {
      return <ShellFrame context={auth.context} navigate={navigate}>
        <CasaNativaAdmin navigate={navigate} />
      </ShellFrame>;
    }
    const organizations = selectableOrganizations(auth.context).map(({ organization }) => ({ slug: organization.slug, displayName: organization.displayName }));
    return <ShellFrame context={auth.context} navigate={navigate}>
      <DemosWorkspace organizations={organizations} navigate={navigate} />
    </ShellFrame>;
  }
  if (route.kind === "demo-detail") {
    if (auth.context.supportSession) return <ShellFrame context={auth.context} navigate={navigate}><Page><StatusState title="Modo soporte activo" message="Salí de la vista cliente antes de abrir el catálogo interno de demos." /></Page></ShellFrame>;
    if (!isPlatformStaff(auth.context)) return <StatusPage eyebrow="Acceso interno" title="Demo no disponible" message="Esta sección es exclusiva del equipo ClickDialog." action={<button onClick={() => navigate("/select-organization")}>Volver a mis negocios</button>} />;
    const organizations = selectableOrganizations(auth.context);
    return <DemoDetailWorkspace
      demoId={route.demoId}
      moduleKey={route.moduleKey}
      nestedPath={route.nestedPath}
      organizations={organizations}
      api={api}
      navigate={navigate}
      onExpired={signedOut}
    />;
  }
  if (route.kind === "prospect-demo") {
    if (auth.context.supportSession) return <ShellFrame context={auth.context} navigate={navigate}><Page><StatusState title="Modo soporte activo" message="Salí de la vista cliente antes de abrir Prospect Demos internos." /></Page></ShellFrame>;
    if (!isPlatformStaff(auth.context)) return <StatusPage eyebrow="Acceso interno" title="Prospect Demo no disponible" message="Esta sección es exclusiva del equipo ClickDialog." action={<button onClick={() => navigate("/select-organization")}>Volver a mis negocios</button>} />;
    return <ShellFrame context={auth.context} navigate={navigate}>
      <ProspectDemoWorkspace
        api={api}
        requestId={route.requestId}
        navigate={navigate}
      />
    </ShellFrame>;
  }
  if (route.kind === "solution-studio") {
    if (auth.context.supportSession) return <ShellFrame context={auth.context} navigate={navigate}><Page><StatusState title="Modo soporte activo" message="Salí de la vista cliente antes de abrir Solution Studio." /></Page></ShellFrame>;
    if (!isPlatformStaff(auth.context)) return <StatusPage eyebrow="Acceso interno" title="Solution Studio no disponible" message="Esta sección es exclusiva del equipo ClickDialog." action={<button onClick={() => navigate("/select-organization")}>Volver a mis negocios</button>} />;
    const targetOrganizations = selectableOrganizations(auth.context).map(
      ({ organization }) => ({
        id: organization.id,
        slug: organization.slug,
        displayName: organization.displayName,
      }),
    );
    return <ShellFrame context={auth.context} navigate={navigate}>
      <SolutionStudio api={api} targetOrganizations={targetOrganizations} />
    </ShellFrame>;
  }
  if (route.kind === "crm") {
    if (auth.context.supportSession) return <ShellFrame context={auth.context} navigate={navigate}><Page><StatusState title="Modo soporte activo" message="Salí de la vista cliente antes de volver al CRM interno." /></Page></ShellFrame>;
    if (!isPlatformStaff(auth.context)) return <StatusPage eyebrow="Acceso interno" title="CRM no disponible" message="Esta sección es exclusiva del equipo ClickDialog." action={<button onClick={() => navigate("/select-organization")}>Volver a mis negocios</button>} />;
    return <ShellFrame context={auth.context} navigate={navigate}>
      <CrmWorkspace route={route} navigate={navigate} onExpired={signedOut} products={auth.context.productRegistry} />
    </ShellFrame>;
  }
  if (route.kind === "organization") return <OrganizationRouteView route={route} context={auth.context} api={api} navigate={navigate} onExpired={signedOut} />;
  return <StatusPage eyebrow="404" title="Page not found" message="That Platform route does not exist." action={<button onClick={() => navigate(auth.context.supportSession ? "/select-organization" : isPlatformStaff(auth.context) ? "/crm" : "/select-organization")}>Volver</button>} />;
}